import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Select, Space, message, Switch, Tag, Modal, Form, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, SearchOutlined, ClearOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import './masters-config.css';

const { Option } = Select;

// Modality Locations master: maps a location name to the DICOM AE Title its
// machines send with. The PACS "Location" filter matches studies on this
// AE Title (ps_sending_aet). Both fields are free text; the same location
// name may repeat across entries when it covers several AE Titles.
//
// Each entry may optionally be tied to a Centre. That association drives the
// Site/Group scoping of the Location dropdown on the PACS and Worklist pages:
// an entry with a centre is only offered while that centre is among the
// selected sites, while an entry left on "All Centres" shows everywhere.
const ModalityLocations = () => {
  const [locationsList, setLocationsList] = useState({ data: [], loading: false, error: null });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [centerFilter, setCenterFilter] = useState('all');
  const [centres, setCentres] = useState([]);

  const [form] = Form.useForm();

  const fetchLocations = useCallback(async () => {
    setLocationsList(prev => ({ ...prev, loading: true, error: null }));
    try {
      // all=true returns active + inactive so admins can manage everything.
      const response = await makeGetCall('/modality-locations?all=true');
      if (response?.data?.success) {
        setLocationsList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch modality locations');
      }
    } catch (error) {
      setLocationsList({ data: [], loading: false, error: error.message || 'Failed to fetch modality locations' });
      message.error('Failed to fetch modality locations');
    }
  }, []);

  const fetchCentres = useCallback(async () => {
    try {
      const res = await makeGetCall('/centres?active=true');
      if (res?.data?.success) {
        setCentres(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch centres:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchCentres();
  }, [fetchLocations, fetchCentres]);

  const centreNameByCode = useMemo(() => {
    const map = {};
    centres.forEach(c => { map[c.code] = c.name; });
    return map;
  }, [centres]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ml_location: record.location,
      ml_ae_title: record.ae_title,
      // undefined (not null) so the Select falls back to its placeholder
      ml_center_code: record.center_code || undefined,
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      // upsertModalityLocation validates location/ae_title on every call, so echo them back.
      const res = await makePostCall('/modality-locations', {
        id: record.id,
        location: record.location,
        ae_title: record.ae_title,
        center_code: record.center_code || null,
        is_active: checked,
      });
      if (res?.data?.success) {
        message.success(`Modality location ${checked ? 'activated' : 'deactivated'}`);
        fetchLocations();
      } else {
        throw new Error(res?.data?.message || 'Failed to update modality location');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update modality location');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...(editingRecord ? { id: editingRecord.id } : {}),
        location: values.ml_location?.trim(),
        ae_title: values.ml_ae_title?.trim(),
        // Empty = global: the location shows under every site.
        center_code: values.ml_center_code || null,
        is_active: values.is_active,
      };

      const res = await makePostCall('/modality-locations', payload);
      if (res?.data?.success) {
        message.success(`Modality location ${editingRecord ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchLocations();
      } else {
        throw new Error(res?.data?.message || 'Failed to save modality location');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to save modality location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setCenterFilter('all');
  };

  const filteredLocations = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (locationsList.data || []).filter(loc => {
      if (statusFilter === 'active' && !loc.is_active) return false;
      if (statusFilter === 'inactive' && loc.is_active) return false;
      if (centerFilter === 'global' && loc.center_code) return false;
      if (centerFilter !== 'all' && centerFilter !== 'global' && loc.center_code !== centerFilter) return false;
      if (search) {
        const centreName = centreNameByCode[loc.center_code] || '';
        const haystack = `${loc.location} ${loc.ae_title} ${loc.center_code || ''} ${centreName}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [locationsList.data, statusFilter, centerFilter, searchText, centreNameByCode]);

  const columns = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      sorter: (a, b) => (a.location || '').localeCompare(b.location || ''),
      render: (location) => (
        <Space size={6}>
          <EnvironmentOutlined style={{ color: '#0f766e' }} />
          <span>{location}</span>
        </Space>
      ),
    },
    {
      title: 'AE Title',
      dataIndex: 'ae_title',
      key: 'ae_title',
      sorter: (a, b) => (a.ae_title || '').localeCompare(b.ae_title || ''),
      render: (aeTitle) => <Tag>{aeTitle}</Tag>,
    },
    {
      title: 'Centre',
      dataIndex: 'center_code',
      key: 'center_code',
      sorter: (a, b) =>
        (centreNameByCode[a.center_code] || a.center_code || '').localeCompare(
          centreNameByCode[b.center_code] || b.center_code || ''
        ),
      render: (code) =>
        code
          ? (centreNameByCode[code] || code)
          : <span className="mc-muted">All Centres</span>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Edit Modality Location">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Modality Locations</span>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Location
        </Button>
      </div>

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by location, AE Title or centre"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Select
            value={centerFilter}
            onChange={setCenterFilter}
            style={{ width: 220 }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Centres</Option>
            <Option value="global">Not centre-specific</Option>
            {centres.map((c) => (
              <Option key={c.code} value={c.code}>
                {c.name} ({c.code})
              </Option>
            ))}
          </Select>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {locationsList.error && (
        <div className="mc-error">
          <p>{locationsList.error}</p>
          <Button onClick={fetchLocations}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Modality Locations</span>
          <span className="mc-count">{filteredLocations.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredLocations}
            loading={locationsList.loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} locations`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit Modality Location' : 'Add Modality Location'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Location"
            name="ml_location"
            tooltip="Shown in the PACS Location filter dropdown. The same name can repeat across entries when one location covers several AE Titles."
            rules={[
              { required: true, message: 'Please enter the location' },
              { max: 100, message: 'Location must be 100 characters or less' },
            ]}
          >
            <Input placeholder="e.g. CT Room 1 - Ground Floor" />
          </Form.Item>

          <Form.Item
            label="AE Title"
            name="ml_ae_title"
            tooltip="The DICOM AE Title the machines at this location send with. Studies are matched against it in the PACS Location filter."
            rules={[
              { required: true, message: 'Please enter the AE Title' },
              { max: 64, message: 'AE Title must be 64 characters or less' },
            ]}
          >
            <Input placeholder="e.g. CT_ROOM1" />
          </Form.Item>

          <Form.Item
            label="Centre"
            name="ml_center_code"
            tooltip="Optional. When set, this location is only offered in the PACS/Worklist Location filter while that centre is among the selected sites. Leave it empty to show it under every site."
          >
            <Select
              placeholder="All Centres (not centre-specific)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {centres.map((c) => (
                <Option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModalityLocations;
